import { Tooltip } from "@mui/material";
import Search from "@mui/icons-material/Search";
import { useMemo, useState } from "react";
import styled from "styled-components/macro";
import { useCustomerKey } from "utils/customHooks/useCustomerKey";
import { lightBlueButton } from "constants/colors";
import { OpenInNew } from "@mui/icons-material";
import { getEndpointUrl } from "./getEndpointUrl";
import { ControlGroupContent } from "./ControlGroupContent";
import { useDebuggerPopupState } from "../debuggerUtils";
import { NetworkConnectionType } from "./useDebuggerGroups";

export const ControlGroup = ({
  title,
  networkMetadata,
  resources,
  dataRequiredOnThisPage,
}: {
  title: "Askr" | "Midgard" | "Auth" | "LaunchDarkly" | "DEFAULT_GROUP";
  networkMetadata: NetworkConnectionType["networkMetadata"];
  resources: NetworkConnectionType["resources"];
  dataRequiredOnThisPage: string[];
}) => {
  const [open, setOpen] = useState(true);
  const isDefault = title !== "DEFAULT_GROUP";

  const ckey = useCustomerKey();
  const iframeUrl = useMemo(
    () => ckey && getEndpointUrl(ckey)[title],
    [ckey, title]
  );

  return (
    <ControlGroupStyles>
      {isDefault && (
        <Heading open={open}>
          {iframeUrl && <DebuggerBtnInspect {...{ iframeUrl, title }} />}
          <div className="title" onClick={() => setOpen(o => !o)}>
            <div className="titleText">{title}</div>
            {networkMetadata && (
              <Tooltip title="unlike updates, requests are only counted while Debugger is opened — refresh with Debugger open for an accurate comparison">
                <div className="titleMeta lightText">
                  <span className={`${title} number numRequests`}>
                    {networkMetadata.requests.length}
                  </span>{" "}
                  requests,{" "}
                  <span className={`${title} number numUpdates`}>
                    {networkMetadata.timesUpdated}{" "}
                  </span>{" "}
                  updates
                </div>
              </Tooltip>
            )}
          </div>
          {networkMetadata?.graphiqlUrl && (
            <a
              className="graphiqlLink"
              href={networkMetadata.graphiqlUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              graphiql <OpenInNew />
            </a>
          )}
        </Heading>
      )}
      {open ? (
        <ControlGroupContent
          isDefault={isDefault}
          items={resources}
          dataRequiredOnThisPage={dataRequiredOnThisPage}
        />
      ) : null}
    </ControlGroupStyles>
  );
};

function DebuggerBtnInspect({ iframeUrl, title }) {
  const [, setDebuggerPopupState] = useDebuggerPopupState();
  return (
    <div
      className="btnInspect"
      onClick={() => {
        setDebuggerPopupState(prev => ({
          ...prev,
          isPreview: false,
          isPopupOpen: true,
          popupData: {
            networkName: title,
            data: null,
            iframeUrl,
          },
        }));
      }}
      onDoubleClick={() => {
        window.open(iframeUrl, "_blank")?.focus();
      }}
    >
      <Tooltip title="Preview API endpoint">
        <Search />
      </Tooltip>
    </div>
  );
}

const ControlGroupStyles = styled.div`
  padding: 6px 0;
  .graphiqlLink {
    text-decoration: none;
    color: darkgray;
    &.active {
      color: ${lightBlueButton};
      text-decoration: underline;
    }
    font-size: 10px;
    svg {
      width: 10px;
      height: 10px;
    }
  }
  .networkItemRow .graphiqlLink {
    position: absolute;
    right: 44px;
    top: 4px;
  }
  .title {
    padding-right: 64px;
    margin-right: -64px;
  }
`;

const Heading = styled.h2<{ open: boolean }>`
  display: block;
  font-family: sans-serif;
  font-size: 13px;
  font-weight: bold;
  padding-left: 16px;
  cursor: pointer;
  position: relative;
  /* disabling user selection inorder to avoid unnecessary text selection or drags */
  user-select: none;
  .btnInspect {
    position: absolute;
    left: -8px;
    color: #999;
    .MuiSvgIcon-root {
      transform: scale(0.7);
      transform-origin: top;
    }
  }
  &:before,
  &:after {
    content: "";
    position: absolute;
    top: 8px;
    right: 16px;
    width: 12px;
    height: 2px;
    background-color: #333;
    /* transition: transform 0.25s ease-out; */
  }
  &:before {
    transform: rotate(${props => (props.open ? 0 : 90)}deg);
  }
  &:after {
    transform: rotate(${props => (props.open ? 0 : 180)}deg);
  }
  .title {
    display: grid;
    grid-template-columns: auto 1fr;
    justify-items: flex-end;
    width: calc(100% - 65px);
  }
  .titleMeta {
    font-weight: normal;
    font-size: 12px;
    .number {
      color: black;
    }
  }
`;
